/**
 * Boundary mask — clip a rectangular terrain raster to an administrative
 * polygon so "DEM of Switzerland" shows elevation ONLY inside Switzerland's
 * border, not the whole bounding rectangle.
 *
 * The terrain overlay renders a colorized DEM to a canvas placed at the DEM's
 * lat/lon bounds (a linear plate-carrée mapping over the bbox — see
 * renderTerrainOverlay). To clip, we draw the boundary polygon in that same
 * pixel space and composite with 'destination-in', which keeps only the pixels
 * under the polygon and makes everything outside transparent. Works for any
 * metric (elevation / slope / aspect / hillshade) since it operates on the
 * finished canvas.
 *
 * The geometry helpers (geoToPixel, eachPolygonRing, boundsOfGeoJSON,
 * pointInMultiPolygon) are pure and unit-tested; maskCanvasToBoundary touches a
 * 2D canvas and is verified in the browser.
 */

/**
 * Map a lon/lat to a pixel in a canvas that spans bbox [w,s,e,n]. Pure.
 * (The mask is drawn in the raster's own EPSG:4326 pixel grid, BEFORE the
 * mercator row-resample in terrain-raster-overlay.js hands the canvas to
 * MapLibre — so this linear lat/lon mapping is exactly right here. CL-236.)
 * @returns {[number, number]} [px, py]
 */
export function geoToPixel(lng, lat, bbox, width, height) {
  const [w, s, e, n] = bbox
  const px = ((lng - w) / (e - w || 1)) * width
  const py = ((n - lat) / (n - s || 1)) * height
  return [px, py]
}

/**
 * Call cb(ring) for every linear ring in a GeoJSON Polygon / MultiPolygon /
 * Feature / FeatureCollection (outer rings AND holes). ring = [[lng,lat], …].
 * Pure — the traversal shape everything else is built on.
 */
export function eachPolygonRing(geojson, cb) {
  if (!geojson) return
  const geom = (g) => {
    if (!g) return
    if (g.type === 'FeatureCollection') return g.features?.forEach((f) => geom(f))
    if (g.type === 'Feature') return geom(g.geometry)
    if (g.type === 'Polygon') return g.coordinates?.forEach((ring) => cb(ring))
    if (g.type === 'MultiPolygon')
      return g.coordinates?.forEach((poly) => poly.forEach((ring) => cb(ring)))
    if (g.type === 'GeometryCollection') return g.geometries?.forEach((x) => geom(x))
  }
  geom(geojson)
}

/**
 * Bounding box [w,s,e,n] of any GeoJSON polygonal object. Pure. Returns null if
 * there are no coordinates.
 */
export function boundsOfGeoJSON(geojson) {
  let w = Infinity
  let s = Infinity
  let e = -Infinity
  let n = -Infinity
  eachPolygonRing(geojson, (ring) => {
    for (const pt of ring) {
      const lng = pt[0]
      const lat = pt[1]
      if (lng < w) w = lng
      if (lng > e) e = lng
      if (lat < s) s = lat
      if (lat > n) n = lat
    }
  })
  return Number.isFinite(w) ? [w, s, e, n] : null
}

/**
 * Bounding box of a region's MAINLAND CLUSTER: the largest polygon part plus
 * every part within `gapDeg` of the growing cluster. Pure.
 *
 * Why: boundsOfGeoJSON over a whole country is dominated by far-flung
 * territories — France's bbox spans the Atlantic (French Guiana), Chile's
 * reaches Easter Island, the USA's crosses the antimeridian (Aleutians) —
 * which makes the raw bbox useless for choosing/covering/framing a DEM
 * (CL-227 audit). Clustering from the largest part keeps contiguous territory
 * (CONUS + Alaska survive a 10° gap) while dropping oceanic outliers (Hawaii,
 * Easter Island, French Guiana). The MASK still uses the full geometry — an
 * outlying island simply falls outside the raster crop instead of dragging the
 * whole view out to sea.
 *
 * @param {object} geojson polygonal GeoJSON
 * @param {number} [gapDeg=10] max bbox-to-bbox gap (degrees) to absorb a part
 * @returns {number[]|null} [w,s,e,n] or null when there are no coordinates
 */
export function mainlandClusterBounds(geojson, gapDeg = 10) {
  // Collect each ring as a part: [bbox, approx area]. Holes only shrink a
  // part, so ranking rings by area still surfaces the true mainland first.
  const parts = []
  eachPolygonRing(geojson, (ring) => {
    if (!ring || ring.length < 3) return
    let w = Infinity
    let s = Infinity
    let e = -Infinity
    let n = -Infinity
    // Shoelace in degree space — only used to RANK parts, so the latitude
    // distortion is irrelevant.
    let area2 = 0
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const x = ring[i][0]
      const y = ring[i][1]
      if (x < w) w = x
      if (x > e) e = x
      if (y < s) s = y
      if (y > n) n = y
      area2 += ring[j][0] * y - x * ring[j][1]
    }
    parts.push({ bbox: [w, s, e, n], area: Math.abs(area2) / 2 })
  })
  if (!parts.length) return null

  parts.sort((a, b) => b.area - a.area)
  let [w, s, e, n] = parts[0].bbox
  const absorbed = new Set([0])
  // Iterate to a fixpoint: absorbing one part can bring another into range.
  let changed = true
  while (changed) {
    changed = false
    for (let i = 1; i < parts.length; i++) {
      if (absorbed.has(i)) continue
      const [pw, ps, pe, pn] = parts[i].bbox
      const dx = Math.max(0, Math.max(w - pe, pw - e))
      const dy = Math.max(0, Math.max(s - pn, ps - n))
      if (dx <= gapDeg && dy <= gapDeg) {
        if (pw < w) w = pw
        if (ps < s) s = ps
        if (pe > e) e = pe
        if (pn > n) n = pn
        absorbed.add(i)
        changed = true
      }
    }
  }
  return [w, s, e, n]
}

/**
 * Even-odd point-in-polygon over every ring (outer rings and holes), so holes
 * correctly punch out. Valid for non-overlapping MultiPolygons (admin parts).
 * Pure — exported for tests and as a CPU fallback if canvas clipping is ever
 * unavailable.
 */
export function pointInMultiPolygon(lng, lat, geojson) {
  let inside = false
  eachPolygonRing(geojson, (ring) => {
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const xi = ring[i][0]
      const yi = ring[i][1]
      const xj = ring[j][0]
      const yj = ring[j][1]
      const intersects = yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi
      if (intersects) inside = !inside
    }
  })
  return inside
}

/**
 * Clip a canvas to a boundary polygon in place: pixels outside the polygon
 * become transparent. Browser-only (2D canvas). `bbox` is the canvas's
 * [w,s,e,n] extent (the DEM's own bounds). Uses 'destination-in' + even-odd
 * fill so island parts and holes both render correctly.
 * @returns {boolean} true if a non-empty path was drawn (i.e. clipping applied)
 */
export function maskCanvasToBoundary(canvas, geojson, bbox) {
  const ctx = canvas.getContext('2d')
  const { width, height } = canvas
  let drew = false
  ctx.save()
  ctx.globalCompositeOperation = 'destination-in'
  ctx.beginPath()
  eachPolygonRing(geojson, (ring) => {
    if (!ring || ring.length < 3) return
    for (let i = 0; i < ring.length; i++) {
      const [px, py] = geoToPixel(ring[i][0], ring[i][1], bbox, width, height)
      if (i === 0) ctx.moveTo(px, py)
      else ctx.lineTo(px, py)
    }
    ctx.closePath()
    drew = true
  })
  if (drew) ctx.fill('evenodd')
  ctx.restore()
  return drew
}
